// Libraries
import React from 'react';

// Supermove
import {Loading} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {UrlFiltersType, useEffect, useNavigationDOM, useQuery, useState} from '@supermove/hooks';
import {Project, ProjectType, ProjectModel, UserModel} from '@supermove/models';
import {ParamsType} from '@supermove/navigation/src/NavigationTypes';

// App
import {ProjectBlockKindType} from '@shared/modules/Project/enums/ProjectBlockKind';
import UserRole from '@shared/modules/User/enums/UserRole';
import {
  getNavigationSections,
  useProjectPageAnalyticsHandler,
} from 'modules/Project/V2/Show/ShowProjectV2Page';
import ShowProjectV2PageMobile from 'modules/Project/V2/Show/ShowProjectV2PageMobile';
import {ProjectPageUrlFiltersType} from 'modules/Project/V2/types/ProjectPageUrlFilters';

// Keeping this separate because we're doing a lot less here than on the project page
const useProjectPageParamHandler = ({
  project,
  params,
  urlFilters,
  navigationSections,
}: {
  project: ProjectModel;
  params: ParamsType;
  urlFilters: UrlFiltersType;
  navigationSections: {name: string; blocks: ProjectBlockKindType[]}[];
}) => {
  useEffect(() => {
    if (!params.block) {
      const navSection = navigationSections[0];
      return urlFilters.handleUpdate({block: navSection.blocks[0]});
    }
  }, [params, params.block, params.showCancelledJobs, urlFilters, navigationSections, project]);
};

const ShowProjectDetailsContent = ({
  project,
  viewer,
  urlFilters,
  refetch,
  refetchAndReset,
  resetKey,
}: {
  project: ProjectModel;
  viewer: UserModel;
  urlFilters: UrlFiltersType<ProjectPageUrlFiltersType>;
  refetch: () => void;
  refetchAndReset: () => void;
  resetKey: number;
}) => {
  const {params} = useNavigationDOM();
  const {isCrewInventoryEnabled, isCostAndCompensationEnabled} = project.organization.settings;
  const initialSections = getNavigationSections({
    projectType: project.projectType,
    isCostAndCompensationEnabled,
    isCrewInventoryEnabled,
    // Hardcoding to the mobile view for the widget
    responsive: {mobile: true, desktop: false, tablet: false},
    isStorage: project.isStorage,
  });
  const navigationSections = initialSections.filter((section) => section.name !== 'Jobs');
  useProjectPageParamHandler({project, params, urlFilters, navigationSections});
  useProjectPageAnalyticsHandler({params, name: 'Project Details Inbox', trackWidget: false});

  const projectPageContentProps = {
    navigationSections,
    project,
    viewer,
    urlFilters,
    refetch,
    refetchAndReset,
    resetKey,
    isStaffAdmin: UserRole.getIsStaffAdmin(viewer.role), // Only used on desktop
  };

  return <ShowProjectV2PageMobile {...projectPageContentProps} isInWidget />;
};

const ProjectDetailsWidget = ({
  urlFilters,
  projectUuid,
}: {
  urlFilters: UrlFiltersType<ProjectPageUrlFiltersType>;
  projectUuid: string;
}) => {
  const [resetKey, setResetKey] = useState(0);

  const {data, loading, refetch} = useQuery(ProjectDetailsWidget.query, {
    fetchPolicy: 'cache-and-network',
    variables: {projectUuid},
  });

  const refetchAndReset = () => {
    refetch();
    setResetKey(resetKey + 1);
  };

  return (
    <Loading loading={loading} as={ShowProjectV2PageMobile.LoadingComponent}>
      {() => {
        return (
          <ShowProjectDetailsContent
            project={data.project}
            viewer={data.viewer}
            urlFilters={urlFilters}
            refetch={refetch}
            refetchAndReset={refetchAndReset}
            resetKey={resetKey}
          />
        );
      }}
    </Loading>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsWidget.query = gql`
  ${Project.getDisplayAllJobsExcludingChildJobs.fragment}
  ${ProjectType.getJobBlocks.fragment}
  ${ShowProjectV2PageMobile.fragment}

  query ProjectDetailsWidget($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      isStorage
      organization {
        id
        settings {
          id
          isCostAndCompensationEnabled
          isCrewInventoryEnabled
        }
      }
      projectType {
        id
        ...ProjectType_getJobBlocks
      }
      ...Project_getDisplayAllJobsExcludingChildJobs
      ...ShowProjectV2PageMobile
    }
    viewer {
      id
      role
    }
  }
`;

export default ProjectDetailsWidget;
